import styles from './ConsumptionSection.module.scss'
import { Banner, Button } from '@boltenergy-be/design-system'
import { Granularity } from 'types/contracts.ts'
import { FormProvider, useForm } from 'react-hook-form'
import GranularityNavigation from 'components/charts/granularity-navigation/GranularityNavigation.tsx'
import ConsumptionChart from 'components/charts/consumption/ConsumptionChart.tsx'
import { GranularityNavigationInputs, GranularityOptions, NavigationItemType } from 'components/charts/granularity-navigation/types.ts'
import { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { Response } from 'types/request.ts'
import { routes } from 'types/routes.ts'
import { useStoreSelector } from 'hooks/store.ts'
import useWindowSize from 'hooks/useWindowSize.tsx'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router'
import { ReactElement, useEffect, useMemo } from 'react'
import { hasSmartMeter, isActiveContract } from 'utils/contracts.ts'
import { isHandledConsumptionTransfoError } from './utils.ts'
import { useGetPreviousBillingCyclePeriodsQuery, useGetVolumesQuery } from 'store/queries/bolt-api/contracts'
import { memoizedGenerateGetVolumesQuery, memoizedGenerateGranularityNavigationItems } from 'pages/app/consumption/utils.ts'
import { ISO_DATETIME_FORMAT } from 'constants/constants.ts'
import { TransfoErrorCodes } from 'types/billShock.ts'
import { ContractResponseCodes } from 'types/errorCodes.ts'
import { ConsumptionSectionProps } from './types.ts'
import { VOLUMES_DELAY_IN_DAYS } from './constants.ts'
import LoadingSkeleton from 'components/loading-skeleton/LoadingSkeleton.tsx'
import EmptyState from 'components/return-later/EmptyState.tsx'
import Link from 'components/link/Link.tsx'
import classNames from 'classnames'
import { selectCurrentBillingContract, selectCurrentServiceContracts, selectIsProducerAccount } from 'store/contact/slice.ts'
import { EL_PRODUCT_CODES, ENERGY_TYPES } from '@boltenergy-be/shared'
import useGetAccessRight from 'hooks/useSelectAccessRight.ts'
import { ACCESS_RIGHT_KEYS } from 'store/access-rights/constants.ts'

const ConsumptionSection = ({ energyType, compact, setHide, chartLayoutClassName }: ConsumptionSectionProps) => {
  // Redux
  const isProducer = useStoreSelector(selectIsProducerAccount)
  const billingContract = useStoreSelector(selectCurrentBillingContract)
  const { electricity, gas } = useStoreSelector(selectCurrentServiceContracts)

  // Contracts
  const currentContract = gas && energyType === ENERGY_TYPES.gas ? gas : electricity

  // Access rights
  const consumptionAccess = useGetAccessRight(ACCESS_RIGHT_KEYS.consumption)
  const meterReadingsAccess = useGetAccessRight(ACCESS_RIGHT_KEYS.meterReadings)

  // Window size
  const { isTablet } = useWindowSize()

  // i18n
  const { t } = useTranslation(['consumption', 'common', 'errors'])

  // React router
  const navigate = useNavigate()
  const { search } = useLocation()
  const query = useMemo<URLSearchParams>(() => new URLSearchParams(search), [search])

  // Contracts
  const isMonthlyBilling = !!(electricity.detail.monthlyBilling && (gas ? gas?.detail?.monthlyBilling : true))
  const granularities: GranularityOptions = memoizedGenerateGranularityNavigationItems({
    contract: currentContract,
    dataDelayInDays: VOLUMES_DELAY_IN_DAYS
  })
  const startAsBillingCycle = !isMonthlyBilling && !!query.get('cycle')

  // React hook form
  const hookForm = useForm<GranularityNavigationInputs>({
    defaultValues: {
      view: startAsBillingCycle ? NavigationItemType.CYCLE : Granularity.MONTH,
      period: startAsBillingCycle
        ? query.get('cycle')!
        : granularities[Granularity.MONTH].periods?.find((period) => period.defaultSelected)?.value
    }
  })
  const watchView = hookForm.watch('view')
  const watchPeriod = hookForm.watch('period')

  // Reset period when there is no matching period for the selected granularity view
  useEffect(() => {
    const currentGranularity = watchView !== NavigationItemType.CYCLE ? granularities[watchView] : undefined
    if (!!currentGranularity?.periods && !currentGranularity.periods.some((period) => period.value === watchPeriod)) {
      const defaultPeriod =
        currentGranularity.periods.find((period) => period.defaultSelected)?.value || currentGranularity.periods[0].value
      hookForm.setValue('period', defaultPeriod)
    }
  }, [granularities, hookForm, watchPeriod, watchView])

  // Redux Queries
  const { data: previousBillingCyclesPeriods, isLoading: isLoadingPreviousBillingCyclesPeriods } = useGetPreviousBillingCyclePeriodsQuery(
    billingContract.contractNumber,
    { skip: !consumptionAccess.showContent || !currentContract.contractNumber }
  )
  const volumesQuery = memoizedGenerateGetVolumesQuery({
    currentView: watchView,
    currentPeriod: watchPeriod,
    billingCyclePeriods: previousBillingCyclesPeriods
  })
  const {
    data,
    isLoading: isVolumesLoading,
    isFetching: isFetchingVolumes,
    isError,
    error
  } = useGetVolumesQuery(
    {
      serviceContractId: currentContract.contractNumber,
      granularity: volumesQuery ? volumesQuery.granularity : Granularity.MONTH,
      from: volumesQuery?.from?.format(ISO_DATETIME_FORMAT) || '',
      until: volumesQuery?.until?.format(ISO_DATETIME_FORMAT) || ''
    },
    {
      skip:
        !consumptionAccess.showContent ||
        !currentContract?.contractNumber ||
        (!previousBillingCyclesPeriods && watchView === NavigationItemType.CYCLE) ||
        !volumesQuery
    }
  )

  // useMemo
  const billingCycles = undefined
  // TODO: temporary disabled
  /*useMemo<{ [key: string]: NavigationItem } | undefined>(
    () => (!isMonthlyBilling ? generatePreviousBillingCycleNavigationItems(activeProductType, previousBillingCyclesPeriods) : undefined),
    [activeProductType, isMonthlyBilling, previousBillingCyclesPeriods]
  )*/

  // Constants
  const hasInjection = !!data?.volumes?.some((entry) => (entry.injection?.billed || entry.injection?.unbilled || 0) !== 0)
  const defaultGranularity = granularities[Granularity.MONTH]
  const selectedNavigationItem =
    watchView !== NavigationItemType.CYCLE
      ? granularities?.[watchView] || defaultGranularity
      : billingCycles
        ? billingCycles[watchPeriod]
        : defaultGranularity
  const showEstimatedUsage = selectedNavigationItem.type === NavigationItemType.GRANULARITY
  const dataTranslation = {
    dataTranslation: t(($) => $.yourConsumption.data[isProducer ? 'producer' : 'consumer']),
    interpolation: { escapeValue: false }
  }

  // When the card contains an error, hide it on the parent
  useEffect(() => {
    if (error && typeof setHide === 'function') {
      setHide(true)
    }
  }, [error, setHide])

  /**
   * Returns the correct consumption error for the given code
   *
   * @param {TransfoErrorCodes | null} code
   * @returns {ReactElement}
   */
  const getConsumptionError = (code?: TransfoErrorCodes | ContractResponseCodes | string): ReactElement => {
    // Own API errors
    if (code === ContractResponseCodes.NO_SMR3_VOLUMES_AVAILABLE_YET) {
      return <Banner type="informative">{t(($) => $.yourConsumption.info.noUsage.SMR3.tooRecent, dataTranslation)}</Banner>
    }

    if ([ContractResponseCodes.NO_VOLUMES, ContractResponseCodes.NO_USABLE_VOLUMES].includes(code as ContractResponseCodes)) {
      const isDigitalMeter = hasSmartMeter(currentContract)

      return (
        <Banner
          type="informative"
          title={
            energyType === ENERGY_TYPES.electricity && electricity.detail.dynamicTariff
              ? t(($) => $.yourConsumption.info.noUsage.SMR3.unavailable, dataTranslation)
              : t(($) => $.yourConsumption.info.noUsage.nonSMR3[isDigitalMeter ? 'digital' : 'analog'], dataTranslation)
          }
        >
          {(energyType === ENERGY_TYPES.gas
            ? meterReadingsAccess.canAccess.edit[ENERGY_TYPES.gas]
            : meterReadingsAccess.canAccess.edit[ENERGY_TYPES.electricity] && !electricity.detail.dynamicTariff) &&
            !isDigitalMeter && (
              <Link representation="button" variant="secondary" href={routes.CONSUMPTION_METER_READINGS}>
                {t(($) => $.yourConsumption.info.noUsage.nonSMR3.link, dataTranslation)}
              </Link>
            )}
        </Banner>
      )
    }

    // API & Transfo errors
    if (isHandledConsumptionTransfoError(code)) {
      return (
        <Banner type="informative">
          <Trans
            i18nKey={($) => $.transfo[code]}
            ns="errors"
            components={[<Link key="support" href={routes.SUPPORT_CONTACT} variant="inline" />]}
          />
        </Banner>
      )
    }

    return (
      <div className={styles.error}>
        {t(($) => $.tryAgain, {
          ns: 'errors'
        })}
      </div>
    )
  }

  return (
    <section className={styles['consumption-section']}>
      {!consumptionAccess.showContent ? (
        <>
          {/* FILTERING + METER READINGS */}
          {!compact && (
            <header className={classNames(styles.header, styles['empty-state-header'])}>
              <FormProvider {...hookForm}>
                <GranularityNavigation disabledFilters billingCycles={billingCycles} {...{ granularities }} />
              </FormProvider>
            </header>
          )}

          {isActiveContract(currentContract) ? (
            <EmptyState description={t(($) => $.yourConsumption.returnLater)} />
          ) : (
            <EmptyState
              title={t(($) => $.emptyState.noData.title, {
                ...dataTranslation,
                ns: 'common'
              })}
              icon="cross"
              description={t(($) => $.emptyState.noData.description, {
                ns: 'common'
              })}
            />
          )}
        </>
      ) : (
        <div className={styles['volumes-wrapper']}>
          {!isVolumesLoading && !isLoadingPreviousBillingCyclesPeriods && !!selectedNavigationItem ? (
            <>
              {/* FILTERING + METER READINGS */}
              {!compact && (
                <header className={styles.header}>
                  <FormProvider {...hookForm}>
                    <GranularityNavigation billingCycles={billingCycles} {...{ granularities }} />
                  </FormProvider>
                </header>
              )}

              {/* CONSUMPTION CHART */}
              {!isError && !error && data ? (
                <>
                  <ConsumptionChart
                    {...{
                      isFetchingVolumes,
                      granularity: watchView === NavigationItemType.CYCLE ? Granularity.MONTH : watchView,
                      hasInjection,
                      isTablet,
                      showEstimatedUsage,
                      compact
                    }}
                    layoutClassName={chartLayoutClassName}
                    data={!isFetchingVolumes ? data.volumes : []}
                    totals={{
                      totalConsumption: data.totalConsumption,
                      totalInjection: data.totalInjection
                    }}
                    activeEnergyType={currentContract.detail.energyType}
                  />

                  {!isFetchingVolumes && data.nonSmr3VolumesAvailable && currentContract.detail.dynamicTariff && (
                    <Banner type="informative">{t(($) => $.yourConsumption.info.noUsage.SMR3.partlyUnavailable)}</Banner>
                  )}
                </>
              ) : (
                getConsumptionError(typeof error === 'string' ? error : ((error as FetchBaseQueryError)?.data as Response)?.message)
              )}

              {!isFetchingVolumes &&
                currentContract.detail?.productCode !== EL_PRODUCT_CODES.Go &&
                watchView === NavigationItemType.CYCLE && (
                  <Button
                    className={styles['billing-cycle-button']}
                    onClick={() =>
                      navigate({
                        pathname: routes.BILLING_CYCLES,
                        search: `cycle=${watchPeriod}`
                      })
                    }
                  >
                    {t(($) => $.chart.back)}
                  </Button>
                )}
            </>
          ) : (
            <LoadingSkeleton className="mb-auto mt-none">
              {!compact && <LoadingSkeleton.Rectangle height={98} />}
              <LoadingSkeleton.Rectangle aspectRatio="3/1" />
              <div className="flex-col gap-200">
                <LoadingSkeleton.Rectangle height={20} />
                <LoadingSkeleton.Rectangle height={20} />
              </div>
            </LoadingSkeleton>
          )}
        </div>
      )}
    </section>
  )
}

export default ConsumptionSection
